import { useState, useMemo, useCallback, useRef, useEffect } from 'react';

export interface InvoiceForFiltering {
  id: number;
  invoiceNumber: string;
  vendor: string;
  seller?: string;
  buyer?: string;
  amount: number;
  date: Date | string;
  dueDate?: Date | string;
  status: string;           // data quality: ready/review/incomplete (internal, drives AI chip logic)
  invoiceStatus?: string | null;    // workflow status: ready_to_process/confirmed/paid/overdue
  invoiceDirection?: string | null; // outgoing/incoming
  validationScore?: number;
  sourceType?: 'uploaded' | 'created';
  [key: string]: any;
}

export interface QuickFilters {
  overdue: boolean;
  dueThisWeek: boolean;
  highValue: boolean;
  readyToProcess: boolean;
  confirmed: boolean;
  sent: boolean;
  paid: boolean;
  uploaded: boolean;
  created: boolean;
}

export interface SortConfig {
  column: keyof InvoiceForFiltering;
  direction: 'asc' | 'desc';
}

export interface AdvancedFilters {
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
  amountRange: {
    min: number | null;
    max: number | null;
  };
  statuses: string[];
}

/**
 * Smart filtering with instant feedback
 * No loading spinners - instant like iOS search
 */
export function useInvoiceFiltering(invoices: InvoiceForFiltering[]) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [quickFilters, setQuickFilters] = useState<QuickFilters>({
    overdue: false,
    dueThisWeek: false,
    highValue: false,
    readyToProcess: false,
    confirmed: false,
    sent: false,
    paid: false,
    uploaded: false,
    created: false
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    column: 'dueDate',
    direction: 'asc'
  });
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>({
    dateRange: { start: null, end: null },
    amountRange: { min: null, max: null },
    statuses: []
  });

  // Debounce search term (300ms like iOS)
  const debounceTimer = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchTerm]);

  // Toggle quick filter
  const toggleQuickFilter = useCallback((filter: keyof QuickFilters) => {
    setQuickFilters(prev => ({
      ...prev,
      [filter]: !prev[filter]
    }));
  }, []);

  // Update sort configuration
  const updateSort = useCallback((column: keyof InvoiceForFiltering) => {
    setSortConfig(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setQuickFilters({
      overdue: false,
      dueThisWeek: false,
      highValue: false,
      readyToProcess: false,
      confirmed: false,
      sent: false,
      paid: false,
      uploaded: false,
      created: false
    });
    setAdvancedFilters({
      dateRange: { start: null, end: null },
      amountRange: { min: null, max: null },
      statuses: []
    });
  }, []);

  // Apply all filters in one pass (performance)
  const filteredInvoices = useMemo(() => {
    let result = [...invoices];

    // Text search (fuzzy match). Buyer is included because the dashboard table
    // renders a Buyer column and the search placeholder offers to match it.
    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      result = result.filter(inv =>
        inv.invoiceNumber?.toLowerCase().includes(searchLower) ||
        inv.vendor?.toLowerCase().includes(searchLower) ||
        inv.seller?.toLowerCase().includes(searchLower) ||
        inv.buyer?.toLowerCase().includes(searchLower) ||
        inv.amount?.toString().includes(searchLower)
      );
    }

    // Quick filters
    if (quickFilters.overdue) {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      result = result.filter(inv => {
        // Anything already settled is not overdue, whatever its due date says.
        // Without this the filtered list included paid invoices and so never
        // matched the headline count it was triggered from.
        if (inv.invoiceStatus === 'paid' || inv.invoiceStatus === 'cancelled') return false;
        if (inv.invoiceStatus === 'overdue') return true;
        if (!inv.dueDate) return false;
        const dueDate = typeof inv.dueDate === 'string' ? new Date(inv.dueDate) : new Date(inv.dueDate);
        dueDate.setHours(0, 0, 0, 0);
        return dueDate < now;
      });
    }

    if (quickFilters.dueThisWeek) {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const weekFromNow = new Date();
      weekFromNow.setDate(weekFromNow.getDate() + 7);
      weekFromNow.setHours(0, 0, 0, 0);

      result = result.filter(inv => {
        if (!inv.dueDate) return false;
        const dueDate = typeof inv.dueDate === 'string' ? new Date(inv.dueDate) : inv.dueDate;
        dueDate.setHours(0, 0, 0, 0);
        return dueDate >= now && dueDate <= weekFromNow;
      });
    }

    if (quickFilters.highValue) {
      result = result.filter(inv => (inv.amount || 0) > 10000);
    }

    // Invoice workflow status filters
    if (quickFilters.readyToProcess) {
      result = result.filter(inv => inv.invoiceStatus === 'ready_to_process');
    }

    if (quickFilters.confirmed) {
      result = result.filter(inv => inv.invoiceStatus === 'confirmed');
    }

    if (quickFilters.sent) {
      result = result.filter(inv => inv.invoiceStatus === 'sent');
    }

    if (quickFilters.paid) {
      result = result.filter(inv => inv.invoiceStatus === 'paid');
    }

    // Source type filters
    if (quickFilters.uploaded) {
      result = result.filter(inv => inv.sourceType === 'uploaded');
    }

    if (quickFilters.created) {
      result = result.filter(inv => inv.sourceType === 'created');
    }

    // Advanced filters - Date range
    if (advancedFilters.dateRange.start || advancedFilters.dateRange.end) {
      result = result.filter(inv => {
        if (!inv.date) return false;
        const invDate = typeof inv.date === 'string' ? new Date(inv.date) : inv.date;

        if (advancedFilters.dateRange.start) {
          const start = new Date(advancedFilters.dateRange.start);
          start.setHours(0, 0, 0, 0);
          if (invDate < start) return false;
        }

        if (advancedFilters.dateRange.end) {
          const end = new Date(advancedFilters.dateRange.end);
          end.setHours(23, 59, 59, 999);
          if (invDate > end) return false;
        }

        return true;
      });
    }

    // Advanced filters - Amount range
    if (advancedFilters.amountRange.min !== null || advancedFilters.amountRange.max !== null) {
      result = result.filter(inv => {
        const amount = inv.amount || 0;
        if (advancedFilters.amountRange.min !== null && amount < advancedFilters.amountRange.min) {
          return false;
        }
        if (advancedFilters.amountRange.max !== null && amount > advancedFilters.amountRange.max) {
          return false;
        }
        return true;
      });
    }

    // Advanced filters - Status
    if (advancedFilters.statuses.length > 0) {
      result = result.filter(inv =>
        advancedFilters.statuses.includes(inv.status?.toLowerCase())
      );
    }

    // Sorting
    result.sort((a, b) => {
      const aVal = a[sortConfig.column];
      const bVal = b[sortConfig.column];
      const multiplier = sortConfig.direction === 'asc' ? 1 : -1;

      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Handle dates
      if (sortConfig.column === 'date' || sortConfig.column === 'dueDate') {
        const aDate = typeof aVal === 'string' ? new Date(aVal) : aVal as Date;
        const bDate = typeof bVal === 'string' ? new Date(bVal) : bVal as Date;
        return ((aDate as Date).getTime() - (bDate as Date).getTime()) * multiplier;
      }

      // Handle numbers
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * multiplier;
      }

      // Handle strings
      return String(aVal).localeCompare(String(bVal)) * multiplier;
    });

    return result;
  }, [invoices, debouncedSearchTerm, quickFilters, sortConfig, advancedFilters]);

  // Get active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (debouncedSearchTerm) count++;
    if (quickFilters.overdue) count++;
    if (quickFilters.dueThisWeek) count++;
    if (quickFilters.highValue) count++;
    if (quickFilters.readyToProcess) count++;
    if (quickFilters.confirmed) count++;
    if (quickFilters.sent) count++;
    if (quickFilters.paid) count++;
    if (quickFilters.uploaded) count++;
    if (quickFilters.created) count++;
    if (advancedFilters.dateRange.start || advancedFilters.dateRange.end) count++;
    if (advancedFilters.amountRange.min !== null || advancedFilters.amountRange.max !== null) count++;
    if (advancedFilters.statuses.length > 0) count++;
    return count;
  }, [debouncedSearchTerm, quickFilters, advancedFilters]);

  return {
    filteredInvoices,
    searchTerm,
    setSearchTerm,
    quickFilters,
    toggleQuickFilter,
    sortConfig,
    updateSort,
    advancedFilters,
    setAdvancedFilters,
    clearAllFilters,
    activeFilterCount
  };
}
